import { Storage } from '@ionic/storage';
import { ModalController } from '@ionic/angular';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Component, Input, OnInit } from '@angular/core';
import { WardrobeService } from './../../services/wardrobe.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import {
  brand,
  category,
  color,
  condition,
  size,
  type,
} from 'src/app/constants/general.constant';
import {
  ProductCategoryModel,
  ProductColorModel,
} from './../../models/wardrobe.model';
import { ChooseLocationModalComponent } from '../choose-location-modal/choose-location-modal.component';
import { ProductCategoryModalComponent } from '../product-category-modal/product-category-modal.component';
import {
  ProductBrandModel,
  ProductConditionModel,
  ProductSizeModel,
  ProductTypeModel,
} from 'src/app/models/wardrobe.model';

@Component({
  selector: 'shc-search-filter-modal',
  templateUrl: './search-filter-modal.component.html',
  styleUrls: ['./search-filter-modal.component.scss'],
})
export class SearchFilterModalComponent implements OnInit {
  form: UntypedFormGroup;
  @Input() type: string;
  @Input() data: string;
  selectedType: ProductTypeModel;
  selectedSize: ProductSizeModel;
  selectedBrand: ProductBrandModel;
  selectedColor: ProductColorModel;
  selectedCategory: ProductCategoryModel;
  productConditions: Array<ProductConditionModel>;
  selectedLocation: {
    location: string;
    latitude: number;
    longitude: number;
  };
  selectedCondition: ProductConditionModel;
  resultCount: number = null;
  private resultCountTimeout: any;

  constructor(
    private modalCtrl: ModalController,
    private localStorage: Storage,
    private wardrobeService: WardrobeService,
    private errorService: ErrorHandlerService,
  ) {}

  ngOnInit() {
    this.form = new UntypedFormGroup({
      price: new UntypedFormControl(null),
      size: new UntypedFormControl(null),
      condition: new UntypedFormControl(null),
      color: new UntypedFormControl(null),
      type: new UntypedFormControl(null),
      brand: new UntypedFormControl(null),
      category: new UntypedFormControl(null),
      location: new UntypedFormControl(null),
      latitude: new UntypedFormControl(null),
      longitude: new UntypedFormControl(null),
    });
    if (this.data) {
      this.patchFilters(this.data);
    }
    this.getProductConditions();
    this.updateResultCount();
  }

  updateResultCount() {
    clearTimeout(this.resultCountTimeout);
    this.resultCountTimeout = setTimeout(() => {
      const params: any = {
        page: 1,
        per_page: 1,
      };
      if (this.selectedColor?.id) { params.color_id = this.selectedColor.id; }
      if (this.selectedBrand?.id) { params.brand_id = this.selectedBrand.id; }
      if (this.selectedType?.id) { params.type_id = this.selectedType.id; }
      if (this.selectedCategory?.id) { params.category_id = this.selectedCategory.id; }
      if (this.selectedSize?.id) { params.size_id = this.selectedSize.id; }
      if (this.selectedCondition?.id) { params.condition_id = this.selectedCondition.id; }
      this.wardrobeService.productsList(params).subscribe(
        (res) => {
          this.resultCount = res?.meta?.total ?? null;
        },
        () => {
          this.resultCount = null;
        },
      );
    }, 300);
  }

  setSelectedCondition(event) {
    const selectedValue = event.target.value;
    this.selectedCondition = this.productConditions?.find(
      (pro) => pro?.rating === selectedValue,
    );
  }
  getProductConditions() {
    this.wardrobeService.conditionsList().subscribe(
      (res) => {
        this.productConditions = res.data.filter((con) => con.rating !== null);
      },
      (error) => {
        this.errorService.handle(error);
      },
    );
  }

  patchFilters(res: any) {
    this.form.patchValue({
      price: res.price,
      size: res.size,
      condition: res.condition,
      location: res.location,
      latitude: res.latitude,
      longitude: res.longitude,
    });
    this.selectedType = res.type;
    this.selectedColor = res.color;
    this.selectedBrand = res.brand;
    this.selectedSize = res.size;
    this.selectedLocation = {
      location: res.location,
      latitude: res.latitude,
      longitude: res.longitude,
    };
    this.selectedCategory = res.category;
  }

  async openProductCategoryModal(value: string) {
    const modal = await this.modalCtrl.create({
      component: ProductCategoryModalComponent,
      componentProps: {
        value,
        typeId: this.selectedType ? this.selectedType?.id : null,
        categoryId: this.selectedCategory ? this.selectedCategory?.id : null,
        conditions: this.productConditions,
      },
      cssClass: 'product-category-modal-create',
      canDismiss: true,
      id: 'productCategory',
    });
    modal.onDidDismiss().then((data) => {
      if (data && data.data) {
        if (value === brand) {
          this.selectedBrand = data.data;
        } else if (value === category) {
          this.selectedCategory = data.data;
          this.selectedSize = null;
        } else if (value === color) {
          this.selectedColor = data.data;
        } else if (value === type) {
          this.selectedType = data.data;
          this.selectedSize = null;
        } else if (value === size) {
          this.selectedSize = data.data;
        } else if (value === condition) {
          this.selectedCondition = data.data;
          this.form.get('condition')?.setValue(data.data.id);
        }
      }
      this.updateResultCount();
      // this.modalCtrl.dismiss();
    });
    return await modal.present();
  }

  async openLocationModal() {
    const modal = await this.modalCtrl.create({
      component: ChooseLocationModalComponent,
      cssClass: 'location-selections-create',
      canDismiss: true,
      id: 'locationModal',
    });
    modal.onDidDismiss().then((data) => {
      if (data && data.data) {
        this.selectedLocation = data.data;
        this.form.patchValue({
          location: data.data.location,
          latitude: data.data.latitude,
          longitude: data.data.longitude,
        });
        this.updateResultCount();
      }
      // this.modalCtrl.dismiss();
    });
    return await modal.present();
  }

  applyFilter() {
    this.form.patchValue({
      color: this.selectedColor,
      type: this.selectedType,
      brand: this.selectedBrand,
      category: this.selectedCategory,
      size: this.selectedSize,
    });
    this.localStorage.set('search-filters', this.form.value);
    this.modalCtrl.dismiss(this.form.value, null, 'searchFilters');
  }

  resetFilter() {
    this.form.reset();
    this.localStorage.set('search-filters', this.form.value);
    this.modalCtrl.dismiss(this.form.value);
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }
}
